import { Box, Checkbox, Chip, Link, Tooltip } from '@mui/material';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { BillingMember, BillingPagination } from '../../../interfaces/billing.rpm';
import { StyledTheme } from '../../../styles/styleTheme';
import { DEFAULT_ROWS_PER_PAGE } from '../../../util/constants';
import { formatDateStringToYYYYMMDD } from '../../../util/dateFormat';
import { getCapitalizedWord } from '../../../util/stringFormat';
import { CompactTable } from '../../common/CompactTable/CompactTable';
import { Column } from '../../common/CustomTable';
import { getStatusChipColor } from '../common/ChipColors';
import { CodeChipLevel, CodeChipList } from '../common/CodeChipList';
import { PatientDetailsModal } from '../common/PatientDetailsModal';
import { RpmLoading } from '../common/RpmLoading';
import { formatInsurance } from '../common/Text';
import { RpmStatus } from '../types/rpms.types';

interface PatientSelectionTableProps {
  onRowSelectionChange: (selectedRows: number[]) => void;
  selectedRows: number[];
  onRowExclusionChange: (excludedRows: { uuid: string; index: number }[]) => void;
  excludedRows: { uuid: string; index: number }[];
  isLoading?: boolean;
  data: BillingMember[];
  pagination: BillingPagination;
  onPageChange: (page: number) => void;
  onRowsPerPageChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onAllRecordsSelected: (allRecordsSelected: boolean) => void;
  allRecordsSelected: boolean;
}

export const PatientSelectionTable: React.FC<PatientSelectionTableProps> = ({
  onRowSelectionChange,
  selectedRows,
  onRowExclusionChange,
  excludedRows,
  isLoading = false,
  data,
  pagination,
  onPageChange,
  onRowsPerPageChange,
  onAllRecordsSelected,
  allRecordsSelected,
}) => {
  // States
  const [selectedPatient, setSelectedPatient] = useState<BillingMember | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Row Selection Handlers
  const handleRowSelection = useCallback(
    (id: number) => {
      const patient = data.find((p) => p.id === id);

      if (patient?.rpmExcluded || !patient?.uuid) {
        return;
      }

      const updatedSelectedRows = selectedRows.includes(id)
        ? selectedRows.filter((rowId) => rowId !== id)
        : [...selectedRows, id];

      onRowSelectionChange(updatedSelectedRows);

      if (allRecordsSelected) {
        const updatedExcludedRows = selectedRows.includes(id)
          ? [...excludedRows, { uuid: patient.uuid, index: id }]
          : excludedRows.filter(({ index }) => index !== id);

        onRowExclusionChange(updatedExcludedRows);
      }
    },
    [
      data,
      selectedRows,
      onRowSelectionChange,
      allRecordsSelected,
      excludedRows,
      onRowExclusionChange,
    ],
  );

  const handleSelectAll = useCallback(async () => {
    if (!!selectedRows.length) {
      onRowSelectionChange([]);
    } else {
      onAllRecordsSelected(true);
    }
  }, [onAllRecordsSelected, onRowSelectionChange, selectedRows]);

  const handleDeselectAll = useCallback(() => {
    onAllRecordsSelected(false);
    onRowExclusionChange([]);
  }, [onAllRecordsSelected, onRowExclusionChange]);

  // Modal Handlers
  const handleOpenModal = useCallback((patient: BillingMember) => {
    setSelectedPatient(patient);
    setIsModalOpen(true);
  }, []);

  const handleCloseModal = useCallback(() => {
    setSelectedPatient(null);
    setIsModalOpen(false);
  }, []);

  // Table Configuration
  const columns: Column[] = useMemo(() => {
    const validPatients = data.filter((row) => !row.rpmExcluded);
    const validPatientsCount = validPatients.length;
    const someValidPatientsSelected =
      selectedRows.length > 0 && selectedRows.length < validPatientsCount;

    return [
      {
        key: 'select',
        label: (
          <Tooltip title='Select/deselect all valid patients across pages' placement='right'>
            <span>
              <Checkbox
                size='small'
                style={{ color: StyledTheme.tealDark }}
                indeterminate={someValidPatientsSelected}
                checked={allRecordsSelected}
                onChange={(event) =>
                  event.target.checked ? handleSelectAll() : handleDeselectAll()
                }
                aria-label='Select Valid Rows'
              />
            </span>
          </Tooltip>
        ),
        align: 'center',
      },
      {
        key: 'id',
        label: 'Patient ID',
      },
      {
        key: 'name',
        label: 'Name',
      },
      {
        key: 'cycleStartDate',
        label: 'Cycle Start Date',
      },
      {
        key: 'cycleEndDate',
        label: 'Cycle End Date',
      },
      {
        key: 'clinic',
        label: 'Clinic',
      },
      {
        key: 'insurance',
        label: 'Payer',
      },
      {
        key: 'cptCodes',
        label: 'Available CPT Codes',
      },
      {
        key: 'icd10Codes',
        label: 'ICD10 Codes',
      },
      {
        key: 'rpmStatus',
        label: 'Status',
      },
      {
        key: 'rpmStatusDetails',
        label: 'Details',
      },
    ];
  }, [data, selectedRows.length, handleSelectAll, handleDeselectAll, allRecordsSelected]);

  // Table Data Transformation
  const tableRows = useMemo(
    () =>
      data.map((patient) => ({
        select: (
          <Checkbox
            size='small'
            style={{
              color: !patient.rpmExcluded ? StyledTheme.tealDark : StyledTheme.grayLight,
            }}
            checked={selectedRows.includes(patient.id)}
            onChange={() => handleRowSelection(patient.id)}
            disabled={patient.rpmExcluded}
          />
        ),
        id: (
          <Link component='button' variant='body2' onClick={() => handleOpenModal(patient)}>
            {patient.memberId}
          </Link>
        ),
        name: patient.firstName + ' ' + patient.lastName,
        cycleStartDate: patient.billingCycleRange?.from
          ? formatDateStringToYYYYMMDD(patient.billingCycleRange.from)
          : patient.lastExportDate
          ? formatDateStringToYYYYMMDD(patient.lastExportDate)
          : '-',
        cycleEndDate: patient.billingCycleRange?.to
          ? formatDateStringToYYYYMMDD(patient.billingCycleRange.to)
          : '-',
        clinic: patient.clinicName,
        insurance: formatInsurance(patient.insuranceCompany, patient.insuranceCompanyId),
        cptCodes: (
          <CodeChipList
            codes={
              Object.entries(patient.clinicPayerConfig?.cptCodesConfig || {})
                .filter(([_, { enabled }]) => !!enabled)
                .map(([code, _]) => ({ code })) || []
            }
          />
        ),
        icd10Codes: <CodeChipList codes={patient.icd10Codes.map((code) => ({ code }))} />,
        rpmStatus: (
          <Chip
            label={getCapitalizedWord(patient.rpmExcluded ? RpmStatus.INVALID : RpmStatus.VALID)}
            sx={getStatusChipColor(patient.rpmExcluded ? RpmStatus.INVALID : RpmStatus.VALID)}
            size='small'
          />
        ),
        rpmStatusDetails: (
          <CodeChipList
            codes={[
              ...patient.rpmExclusionCriteria.map((code) => ({
                code,
                level: 'error' as CodeChipLevel,
              })),
              ...patient.rpmWarnings.map((code) => ({ code, level: 'warning' as CodeChipLevel })),
            ]}
            allowSlice={false}
          />
        ),
      })),
    [data, selectedRows, handleRowSelection, handleOpenModal],
  );

  useEffect(() => {
    if (allRecordsSelected) {
      const allValidIds = data.filter((member) => !member.rpmExcluded).map(({ id }) => id);
      onRowSelectionChange(allValidIds);
    } else {
      onRowSelectionChange([]);
    }
  }, [allRecordsSelected, data, onRowSelectionChange]);

  return (
    <>
      <Box position='relative'>
        {isLoading && <RpmLoading />}
        <CompactTable
          columns={columns}
          rows={tableRows}
          defaultText='No patients found'
          pagination={{
            rowsPerPageOptions: DEFAULT_ROWS_PER_PAGE,
            rowPerPage: pagination.limit,
            page: pagination.currentPage,
            totalCount: pagination.totalCount,
            onPageChange,
            onRowsPerPageChange,
          }}
        />
      </Box>

      <PatientDetailsModal
        open={isModalOpen}
        onClose={handleCloseModal}
        patient={selectedPatient}
      />
    </>
  );
};
